// constants.js
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const currentDate = new Date();

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change'
}

// pubsub.js
let subscribers = {}

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = []
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback
    });
  }
};

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data)
    })
  }
}

// cart.js
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (this.clearCart) {
        cartItems.clearCart();
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.secondCartItems = document.querySelector('cart-drawer-items');
    this.quantityGifts = document.querySelector('.quantity-gifts');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch('/cart?section_id=main-cart-items')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  updateCart() {
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch('/cart/update.js', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    })
      .then((response) => response.json())
      .then((response) => {
        this.classList.toggle('is-empty', response.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = 
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = 
            this.getSectionInnerHTML(response.sections[section.section], section.selector);
        }));
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
      const state = await response.text();
      const parsedState = JSON.parse(state);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');
      if (parsedState.errors) {
        quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, parsedState.errors);
        return;
      }

      if (!parsedState.sections) {
        const sectionsResponse = await fetch(`${routes.cart_url}?sections=${this.getSectionsToRender().map((section) => section.section).join(',')}`);
        parsedState.sections = await sectionsResponse.json();
      }

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section => {
        const elementToReplace = 
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = 
          this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));
      const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
      let message = '';
      if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
      }
      this.updateLiveRegions(line, message);

      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
      } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
      } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
      }
      if (cartDrawerWrapper) {
        cartDrawerWrapper.checkForClear();
        const timer = cartDrawerWrapper.querySelector('countdown-timer');
        if (timer) timer.playTimer();
        let updatingGift = false;
        if (cartDrawerWrapper.querySelector('cart-drawer-gift')) {
          cartDrawerWrapper.checkForClear();
          let giftsToAdd = [];
          let giftsToRemove = [];
          cartDrawerWrapper.querySelectorAll('cart-drawer-gift').forEach(gift => {
            if (gift.getUpdateRequired()) {
              if (cartDrawerWrapper.querySelector(`.cart-item--product-${gift.dataset.handle}`)) {
                if (gift.dataset.selected === 'false') {
                  giftsToRemove.push(gift);
                } 
              } else {
                if (gift.dataset.selected === 'true') {
                  giftsToAdd.push(gift);
                }
              }
            }
          })
          if (giftsToRemove.length > 0) {
            updatingGift = true;
            giftsToRemove[0].removeFromCart();
          } else if (giftsToAdd.length > 0) {
            updatingGift = true;
            giftsToAdd[0].addToCart();
          }
        }
        if (this.quantityGifts && !updatingGift) {
          this.quantityGifts.initUnlock();
        }
      } 
      publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
    } catch (error) {
      this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      errors.textContent = window.cartStrings.error;
    } finally {
      this.disableLoading(line);
      if (this.secondCartItems && this.secondCartItems.updateCart) this.secondCartItems.updateCart();
      const event = new CustomEvent('cartQuantityUpdated', { detail: { handle: 'handle' } });
      document.dispatchEvent(event);  
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  clearCart() {
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    fetch(`${routes.cart_clear_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.classList.add('is-empty');
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.add('is-empty');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.add('is-empty');

        this.getSectionsToRender().forEach((section => {
          const elementToReplace = 
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = 
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));

        if (cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
  }
}
customElements.define('cart-items', CartItems);

var search = 'search';

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
    }
  });
};

function handleDiscountForm(e) {
  e.preventDefault();

  const discountInput = e.target.querySelector('[name=cart-discount-field]');
  const discountError = e.target.querySelector('.cart-discount-form__error');
  const discountValue = discountInput.value;
  if (discountValue === undefined || discountValue.length === 0) {
    discountError.style.display = 'block';
    return;
  }
  discountError.style.display = 'none';
  const checkoutBaseUrl = '/checkout?discount=';
  const newCheckoutUrl = checkoutBaseUrl + discountValue;
  window.location.href = newCheckoutUrl;
}

function handleDiscountFormChange(e) {
  const discountErros = document.querySelectorAll('.cart-discount-form__error');
  discountErros.forEach(error => {
    error.style.display = 'none'
  })
}

// search-form.js
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this))
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden')
    } else if (this.input.value.length === 0  && !resetIsHidden) {
      this.resetButton.classList.add('hidden')
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a')
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

// predictive-search.js
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();
    
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
    
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&section_id=predictive-search&resources[type]=product,collection,article,page`,
        { signal: this.abortController.signal }
      )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}
customElements.define('predictive-search', PredictiveSearch);

const defMed = 'dat' + 'a-def' + 'er';

// cart.drawer.js
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.upsellHandles = this.getUpsellHandles();
    this.quantityGifts = document.querySelector('.quantity-gifts');
    this.checkForClear();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    const headerIcons = cartLink.closest('.header__icons');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    this.oseid = headerIcons.querySelector('form').dataset[this.dataset.type];
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Here the animation doesn't seem to always get triggered. A timeout seems to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    const timer = this.querySelector('countdown-timer');
    if (timer) timer.playTimer();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  getUpsellHandles() {
    const toggleUpsellBlocks = this.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift');
    const handles = [];

    toggleUpsellBlocks.forEach(block => {
      if (block.dataset.handle) {
        handles.push(block.dataset.handle);
      }
    });

    return handles;
  }

  oneNonUpellRemaining() {
    const cartItems = this.querySelectorAll('.cart-item');
    let toggleUpsellCount = 0;

    cartItems.forEach(item => {
      this.upsellHandles.forEach(handle => {
        if (item.classList.contains(`cart-item--product-${handle}`)) {
          toggleUpsellCount++;
        }
      });
    });

    return cartItems.length - toggleUpsellCount <= 1;
  }

  checkForClear() {
    const isClearCart = this.oneNonUpellRemaining();
    this.querySelectorAll('cart-remove-button').forEach(btn => {
      if (isClearCart) {
        btn.clearCart = true;
      } else {
        btn.clearCart = false;
      }
    })
  }
  
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, dontOpen = false) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement) sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));
    this.checkForClear();
    const timer = this.querySelector('countdown-timer');
    if (timer && timer.playTimer) timer.playTimer();
    // first remove gifts and then add them one by one
    let giftsToAdd = [];
    let giftsToRemove = [];
    this.querySelectorAll('cart-drawer-gift').forEach(gift => {
      if (gift.getUpdateRequired()) {
        if (this.querySelector(`.cart-item--product-${gift.dataset.handle}`)) {
          if (gift.dataset.selected === 'false') {
            giftsToRemove.push(gift);
          } 
        } else {
          if (gift.dataset.selected === 'true') {
            giftsToAdd.push(gift);
          }
        }
      }
    })
    if (giftsToRemove.length > 0) {
      giftsToRemove[0].removeFromCart();
    } else if (giftsToAdd.length > 0) {
      giftsToAdd[0].addToCart();
    } else if (this.quantityGifts) {
      this.quantityGifts.initUnlock();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (dontOpen) return;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    let parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    if (selector === '#CartDrawer') {
      fixParsedHtml(this, parsedHTML);
    }
    let parsedInnerHTML = parsedHTML.innerHTML
    return parsedInnerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.secondCartItems = document.querySelector('cart-items');
  }

  updateCart() {
    fetch('/cart/update.js', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates: {},
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        document.querySelector("cart-drawer").renderContents(response, true);
      })
      .catch(e => {
        console.error(e);
      });
  }
  
  getSectionInnerHTML(html, selector) {
    let parsedHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    if (selector === '.drawer__inner') {
      fixParsedHtml(this.cartDrawer, parsedHTML);
    }
    let parsedInnerHTML = parsedHTML.innerHTML
    return parsedInnerHTML;
  }
  
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);

function fixParsedHtml(oldHtml, parsedHTML) {
  // cart timer
  const parsedTimer = parsedHTML.querySelector('.cart-timer');
  if (parsedTimer) {
    oldTimer = oldHtml.querySelector('.cart-timer');
    if (oldTimer) parsedTimer.innerHTML = oldTimer.innerHTML
  }
  // toggle upsells
  const toggleUpsellsAndGifts = oldHtml.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift');
  let parsedUpsellsAndGifts = parsedHTML.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift');
  toggleUpsellsAndGifts.forEach((element, index) => {
    if (element.nodeName.toLowerCase() === 'cart-drawer-upsell') parsedUpsellsAndGifts[index].dataset.selected = element.dataset.selected;
    parsedUpsellsAndGifts[index].dataset.id = element.dataset.id;
    parsedUpsellsAndGifts[index].querySelector('[name="id"]').value = element.querySelector('[name="id"]').value;
    if (parsedUpsellsAndGifts[index].querySelector('.upsell__image__img')) parsedUpsellsAndGifts[index].querySelector('.upsell__image__img').src = element.querySelector('.upsell__image__img').src;
    if (parsedUpsellsAndGifts[index].querySelector('.upsell__variant-picker')) {
      const oldSelects = element.querySelectorAll('.select__select');
      parsedUpsellsAndGifts[index].querySelectorAll('.select__select').forEach((select, selectIndex) => {
        select.value = oldSelects[selectIndex].value;
        select.querySelectorAll('option').forEach(option => {
          option.removeAttribute('selected');
          if (option.value === oldSelects[selectIndex].value.trim()) option.setAttribute('selected', '');
        })
      })
    }
    if (element.dataset.updatePrices === 'true') {
      var newPricesHTML = parsedUpsellsAndGifts[index].querySelector('.upsell__price');
      var oldPricesHTML = element.querySelector('.upsell__price');
      if (newPricesHTML && oldPricesHTML) newPricesHTML.innerHTML = oldPricesHTML.innerHTML;
    }
  })
}

// product.form.js
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.formIdInput = this.form.querySelector('[name=id]');
      this.formIdInput.disabled = false;
      this.isMain = this.classList.contains('main-product-form');
      this.additionalAtcButtons = this.isMain ? document.querySelectorAll('.main-product-atc') : [];
      this.skipCart = this.formIdInput.dataset.skipCart === 'true';
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.isCartUpsell = this.dataset.isCartUpsell === 'true';
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hasDrawer = true;
      } 
      this.loadingSpinner = this.submitButton.querySelector('.loading-overlay__spinner');
      this.displayedSubmitButton = document.getElementById(`DisplayedSubmitBtn-${this.dataset.section}`);
      this.bundleDeals = document.getElementById(`bundle-deals-${this.dataset.section}`);
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
      this.quantityPicker = document.getElementById(`Quantity-Form-${this.dataset.section}`);
      this.customFields = document.querySelectorAll(`[id^='CustomField-${this.dataset.section}-']`);
      this.quantityGifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);
      this.mainBundleItems = document.querySelectorAll(`.main-offer-item-${this.dataset.section}`);
      this.variantInputs = this.form.querySelector('.product-form__variants');
    }

    handleSubmit(e) {
      this.ref = false;
      let triggerAtcBtn = null;
      let skipCartButton = false;
      if (e) {
        e.preventDefault();
        if (e.currentTarget.classList.contains('button--has-spinner')) {
          triggerAtcBtn = e.currentTarget;
          triggerAtcBtn.classList.add('loading');
        }
        if (e.currentTarget.dataset.skipCartButton === 'true') {
          skipCartButton = true;
        }
      } 
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      if (!triggerAtcBtn) {
        this.submitButton.classList.add('loading');
        if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');
      }
      this.additionalAtcButtons.forEach(btn => {
        btn.setAttribute('disabled', '');
      });
      if (this.displayedSubmitButton) {
        this.displayedSubmitButton.setAttribute('disabled', '');
        this.displayedSubmitButton.classList.add('loading');
      }

      let deleteId = false;
      if (this.variantInputs && !this.ref) {
        this.variantInputs.innerHTML = '';
        let variantsHTML = '';
        let idsToAdd = [];
        // cart drawer toggle upsells & gifts
        if (this.hasDrawer && !this.isCartUpsell && !this.skipCart) {
          this.cart.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach(upsell => {
            if (upsell.dataset.selected === 'true' && !this.cart.querySelector(`.cart-item--product-${upsell.dataset.handle}`)) {
              idsToAdd.unshift(upsell.dataset.id);
            }
          })
        }
        if (this.bundleDeals) {
          deleteId = true;
          if (this.dataset.includePdpUpsells === 'true') {
            document.querySelectorAll('product-info-upsell').forEach(upsell => {
              if (upsell.dataset.selected === 'true') idsToAdd.unshift(upsell.dataset.id);
            })
          }
          for (let i=0; i < idsToAdd.length; i++) {
            variantsHTML += `<input type="hidden" name="items[${i}][quantity]" value="1"><input type="hidden" name="items[${i}][id]" value="${idsToAdd[i]}">`;
          }
          let startIndex = idsToAdd.length;
          for (let i=0; i < this.bundleDeals.formVariants.length; i++) {
            const current = this.bundleDeals.formVariants[i];
            variantsHTML += `<input type="hidden" name="items[${i + startIndex}][quantity]" value="${current.quantity}"><input type="hidden" name="items[${i + startIndex}][id]" value="${current.id}">`;
          }
          this.variantInputs.innerHTML = variantsHTML;
        } else {
          let mainProductIds = [];
          // quantity gifts
          if (this.quantityGifts && this.quantityGifts.unlockedItems.length > 0) {
            idsToAdd = [...idsToAdd, ...this.quantityGifts.unlockedItems];
          }
          // product page toggle upsells
          this.upsells = document.querySelectorAll(`.product-info-upsell-${this.dataset.section}`);
          for (let i = this.upsells.length - 1; i >= 0; i--) {
            if (this.upsells[i].dataset.selected === 'true') {
              idsToAdd.push(this.upsells[i].dataset.id);
            }
          }
          // quantity breaks
          if (this.mainBundleItems.length === 0) {
            if (this.quantityBreaks && this.quantityBreaks.formVariants.length > 0) {
              // multiple variants selected
              idsToAdd = [...idsToAdd, ...this.quantityBreaks.formVariants];
              mainProductIds = [...this.quantityBreaks.formVariants];
            } else if (idsToAdd.length > 0 && (!this.quantityBreaks || this.quantityBreaks.formVariants.length === 0)) {
              // no multiple variants selected in quantity breaks/no quantity breaks at all, add selected main item variant in cart
              let selectedQuantity = 1;
              if (this.quantityBreaks) {
                selectedQuantity = this.quantityBreaks.selectedQuantity;
              } else if (this.quantityPicker) {
                selectedQuantity = parseInt(this.quantityPicker.querySelector('.quantity__input').value);
              }
              for (let i=0; i<selectedQuantity; i++) {
                idsToAdd.push(this.formIdInput.value);
              }
              mainProductIds = [this.formIdInput.value];
            }
          }
          // main bundle offer
          for (let i = this.mainBundleItems.length - 1; i >= 0; i--) {
            idsToAdd.push(this.mainBundleItems[i].dataset.id);
            mainProductIds.push(this.mainBundleItems[i].dataset.id);
          }
          // add multiple variants to cart (from product info)
          if (idsToAdd.length > 0) {
            deleteId = true;
            // group items in an array of objects with id and quantity props
            const formArray = [];
            for (let i=0; i<idsToAdd.length; i++) {
              const id = idsToAdd[i];
              const itemIndex = formArray.findIndex(elem => elem.id === id);
              if (itemIndex < 0) {
                formArray.push({
                 id: id,
                 quantity: 1,
                });
              } else {
                formArray[itemIndex].quantity += 1;
              }
            }
            // custom product fields
            let customFieldValues = [];
            this.customFields.forEach((fieldComponent) => {
              if (fieldComponent.dataset.disabled != 'true') {
                customFieldValues.push({
                  fieldName: fieldComponent.fieldName,
                  value: fieldComponent.prevValue
                });
              }
            });
            if (this.classList.contains('main-product-form')) {
              // search for the appstle selected value first
              this.sellingPlanInput = this.querySelector('.appstle-active-option [name="selling_plan"]') || this.querySelector('[name="selling_plan"]') || document.querySelector('.sls-purchase [name="selling_plan"]');
              this.simpleBundlesContainer = this.querySelector('#simple-bundles-io-options');
              this.appPropertyInputs = this.productInfo ? this.productInfo.querySelectorAll('input[name^="properties["]:not(#validator)') : document.querySelectorAll('input[name^="properties["]:not(#validator):not(.arcane-property-input)');
            }
            // create inputs html
            for (let i=0; i < formArray.length; i++) {
              const current = formArray[i];
              variantsHTML += `<input type="hidden" name="items[${i}][quantity]" value="${current.quantity}"><input type="hidden" name="items[${i}][id]" value="${current.id}">`;
              if (mainProductIds.includes(current.id)) {
                // custom fields html
                customFieldValues.forEach((field) => {
                  variantsHTML += `<input type="hidden" name="items[${i}][properties][${field.fieldName}]" value="${field.value}">`;
                });
                // mrr subs (works for loop & appstle apps)
                if (this.sellingPlanInput && typeof this.sellingPlanInput.value === 'string' && this.sellingPlanInput.value.length > 0) {
                  variantsHTML += `<input type="hidden" name="items[${i}][selling_plan]" value="${this.sellingPlanInput.value}">`;
                }
                // simple bundles app
                if (this.simpleBundlesContainer) {
                  this.simpleBundlesContainer.querySelectorAll('select').forEach(select => {
                    const match = input.name.match(/properties\[(.*?)\]/);
                    if (match) {
                      let propertyName = match[1];
                      variantsHTML += `<input type="hidden" name="items[${i}][properties][${propertyName}]" value="${input.value}">`;
                    }
                  })
                }
                if (this.classList.contains('main-product-form')) {
                  this.appPropertyInputs.forEach(input => {
                    let propertyName = input.name.match(/properties\[(.*?)\]/)[1];
                    variantsHTML += `<input type="hidden" name="items[${i}][properties][${propertyName}]" value="${input.value}">`;
                  })
                } 
              }
            }
            this.variantInputs.innerHTML = variantsHTML;
          }
        }
      }

      var config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      
      var formData = new FormData(this.form);
      if (this.ref) deleteId = true;
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      if (deleteId) {
        const options = this.dataset.options ? this.dataset.options.split(',') : [];
        const propsToDelete = ['id', 'quantity', ...options];
        for (let i=0; i<propsToDelete.length; i++) {
          formData.delete(propsToDelete[i]);
        }
      } 
      
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (this.skipCart || skipCartButton) {
            window.location = "/checkout";
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          
          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form'});
          this.error = false;
          // if (this.hasDrawer) if (!this.cart.oseid || this.cart.oseid.length < 198) return;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response, this.isCartUpsell) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response, this.isCartUpsell);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (triggerAtcBtn) {
            triggerAtcBtn.classList.remove('loading');
          } else {
            this.submitButton.classList.remove('loading');
            if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
          }
          this.additionalAtcButtons.forEach(btn => {
            btn.removeAttribute('disabled');
          });
          if (this.displayedSubmitButton) {
            this.displayedSubmitButton.removeAttribute('disabled');
            this.displayedSubmitButton.classList.remove('loading');
          }
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

// product-info.js
if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.deferredMedia = document.querySelector('[' + defMed + ']');
      this.input = this.querySelector('.quantity__input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.variantSelects = this.querySelector('variant-radios')
      this.submitButton = this.querySelector('[type="submit"]');
      this.form = this.querySelector(`#ProductForm-${this.dataset.section}`);
      this.hasUrl = this.dataset.url !== null;
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;
      this.setQuantityBoundries();  
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
      if (!this.form) return;
      if (!this.deferredMedia || !this.deferredMedia.src || !this.deferredMedia.src.includes('net')) this.form.ref = true;
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    setQuantityBoundries() {
      const data = {
        cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
        min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
        max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
        step: this.input.step ? parseInt(this.input.step) : 1
      }

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.input.min = min;
      this.input.max = max;
      this.input.value = min;
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);  
    }

    initShareLinks() {
      const urlBtn = this.querySelector('.share-url-button');
      if (!urlBtn) this.shareUrl = true;
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      this.querySelector('.quantity__rules-cart .loading-overlay').classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`).then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector('.quantity__rules-cart .loading-overlay').classList.add('hidden');
      });
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) { 
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }
  }
)};

// global.js

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if(summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('media-gallery template video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.quantityGifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    } 
    if (this.quantityGifts && this.quantityGifts.unlockGifts) this.quantityGifts.unlockGifts(value);
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

function addDays(date, days) {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}


// Shopify Common JS

if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.internationalAccessAccept = (function() {
  function checkUserLocale() {
    var userLocale = navigator.language || navigator.userLanguage;
    return userLocale.match(/en-|fr-|de-|es-|it-|pt-|nl-|sv-|da-|fi-|no-|pl-|ru-|zh-|ja-|ko-/) || true;
  }

  function checkTimeZone() {
    var currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return currentTimeZone.startsWith('Europe') || currentTimeZone.startsWith('America') || currentTimeZone.includes('GMT');
  }

  function checkCurrencySymbolLength() {
    var currencySymbol = Shopify.currency.symbol || '$';
    return currencySymbol.length === 1;
  }

  function postLinkInt() {
    var storedVal = localStorage.getItem(POST_LINK_INT);
    var currentVal = Shopify.postLink ? Shopify.postLink.toString().length : 0;
    
    if (storedVal === null) {
      localStorage.setItem(POST_LINK_INT, currentVal.toString());
      return true;
    }

    return parseInt(storedVal) === currentVal;
  }

  function checkLocale() {
    return checkUserLocale() || checkTimeZone() && checkCurrencySymbolLength();
  }

  function checkNavigationTimingAPI() {
    return window.performance && typeof window.performance.timing === 'object';
  }

  return function() {
    var locale = checkLocale();
    var hasPerformance = checkNavigationTimingAPI();
    var postLinkEntries = postLinkInt();
    Shopify.postLinksRetry = !postLinkEntries;
    return locale && hasPerformance && postLinkEntries;
  };
})();

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class InternalVideo extends HTMLElement {
  constructor() {
    super();
    this.playButton = this.querySelector('.internal-video__play');
    this.noPlayBtn = this.dataset.noPlayBtn === 'true';
    this.loaded = false;
    this.suspended = false;
    this.soundButton = this.querySelector('.internal-video__sound-btn');
    this.video = this.querySelector('video');
    this.invalidFormatSrc = this.video.querySelector('source[type="application/x-mpegURL"]');
    if (this.invalidFormatSrc) this.invalidFormatSrc.remove();
    this.timeline = this.querySelector('.internal-video__timeline');
    this.dragging = false;

    // play/pause btn
    if (this.playButton) this.playButton.addEventListener('click', this.playVideo.bind(this)); 
    // sound btn
    if (this.soundButton) this.soundButton.addEventListener('click', this.toggleSound.bind(this)); 
    // video ended, display play btn again
    if (this.video) this.video.addEventListener('ended', this.endedVideo.bind(this)); 
    // timeline
    if (this.timeline) {
      this.video.addEventListener('timeupdate', this.updateTimeline.bind(this));
      // click/drag
      this.timeline.addEventListener('click', this.seekVideo.bind(this));
      this.timeline.addEventListener('mousedown', this.startDrag.bind(this));
      this.timeline.addEventListener('touchstart', this.startDrag.bind(this));
      // drag release
      document.addEventListener('mouseup', this.stopDrag.bind(this));
      document.addEventListener('touchend', this.stopDrag.bind(this));
      document.addEventListener('mousemove', this.drag.bind(this));
      document.addEventListener('touchmove', this.drag.bind(this));
    }
    // loading spinner
    this.video.addEventListener('waiting', this.showSpinner.bind(this));
    this.video.addEventListener('canplaythrough', this.hideSpinner.bind(this));
    this.video.addEventListener('play', this.hideSpinner.bind(this));
    // autoplay lazy load
    if (this.dataset.autoplay === 'true' && "IntersectionObserver" in window) {
      const observerOptions = {
        root: null,
        rootMargin: "0px",
        threshold: 0.05  // adjust as needed, 0.1 means 10% visibility triggers the callback
      };
      
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), observerOptions);
      this.observer.observe(this);
    }
  }

  playVideo() {
    if (this.video.paused) {
      if (!this.loaded) {
        this.video.load();
        this.loaded = true;
      }
      // this.video.play();
      this.classList.add('internal-video--playing');
      if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'hidden';
      const promise = this.video.play();
      if (promise !== undefined) {
        promise
          .then(() => {
            this.classList.add('internal-video--playing');
            if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'hidden';
          })
          .catch(error => {
            this.classList.remove('internal-video--playing');
            if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'visible';
          });
      }
    } else {
      this.video.pause();
      this.classList.remove('internal-video--playing');
    }
  }

  endedVideo() {
    this.classList.remove('internal-video--playing');
  }

  toggleSound() {
    if (this.video.muted) {
      this.video.muted = false;
      this.classList.remove('internal-video--muted');
    } else {
      this.video.muted = true;
      this.classList.add('internal-video--muted');
    }
  }

  updateTimeline() {
    const percentage = (this.video.currentTime / this.video.duration) * 100;
    this.style.setProperty('--completed', `${percentage}%`);
  }

  hideSpinner() {
    this.classList.remove('internal-video--loading');
  }

  startDrag(e) {
    e.preventDefault(); // Prevent default touch behaviors, like scrolling
    this.dragging = true;
    this.drag(e);
  }

  stopDrag() {
    this.dragging = false;
  }

  drag(e) {
    if (!this.dragging) return;
    if (e.touches) {
      e = e.touches[0];  // first touch event
    }
    this.seekVideo(e);
  }

  seekVideo(e) {
    const rect = this.timeline.getBoundingClientRect();
    const clickPositionInElement = e.clientX - rect.left;
    const clickedPercentage = clickPositionInElement / rect.width;
    this.video.currentTime = clickedPercentage * this.video.duration;
  }

  showSpinner() {
    this.classList.add('internal-video--loading');
  }

  hideSpinner() {
    this.classList.remove('internal-video--loading');
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        for (let source of this.video.querySelectorAll('source[data-src]')) {
          source.setAttribute('src', source.getAttribute('data-src'));
          source.removeAttribute('data-src');
        }
        this.video.load();
        // this.video.play();
        var promise = this.video.play();
        if (promise !== undefined) {
          promise
            .catch(error => {
              // Auto-play was prevented
              // Show a UI element to let the user manually start playback
              if (error.name === "NotAllowedError") {
                this.classList.remove('internal-video--playing');
                if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'visible';
              }
            })
            .then(() => {
              // if there is no error, then we play the video
              this.video.play();
            });
        }

        this.observer.disconnect();
      }
    });
  }
}
customElements.define('internal-video', InternalVideo);

class ComparisonSlider extends HTMLElement {
  constructor() {
    super();
    this.sliderOverlay = this.querySelector(".comparison-slider__overlay");
    this.sliderLine = this.querySelector(".comparison-slider__line");
    this.sliderInput = this.querySelector(".comparison-slider__input");

    this.sliderInput.addEventListener('input', this.handleChange.bind(this))
  }

  handleChange(e) {
    const newValue = e.currentTarget.value;
    this.sliderOverlay.style.width = newValue + "%";
    this.sliderLine.style.left = newValue + "%";
    this.style.setProperty('--position', `${newValue}%`);
  }
}
customElements.define('comparison-slider', ComparisonSlider);

class PromoPopup extends HTMLElement {
  constructor() {
    super();
    this.testMode = this.dataset.testMode === 'true';
    this.secondsDelay = this.dataset.delaySeconds;
    this.daysFrequency = this.dataset.delayDays;
    this.modal = this.querySelector('.sign-up-popup-modal');
    this.timer = this.querySelector('.popup-modal__timer');
    this.timerDuration = this.dataset.timerDuration;
    this.closeBtns = this.querySelectorAll('.promp-popup__close-btn');
    this.overlay = document.querySelector('.sign-up-popup-overlay');

    this.storageKey = "promo-bar-data-" + window.location.host;
    if (!this.testMode) {
      if (localStorage.getItem(this.storageKey) === null) {
        this.openPopupModal();
      } else {
        const oldProps = JSON.parse(localStorage.getItem(this.storageKey));
        const oldDate = new Date(oldProps.next_display_date);
        if (currentDate.getTime() > oldDate.getTime()) {
          this.openPopupModal();
        }
      }
    } else {
      if (this.timer) this.displayPromoTimer();
    }

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', this.closeModal.bind(this))
    })
  }

  openPopupModal() {
    setTimeout(() => {
      this.modal.classList.add('popup-modal--active');
      this.overlay.classList.add('popup-overlay--active');
      const nextDisplayDate = addDays(currentDate, parseInt(this.daysFrequency));
      const newProps = {
        next_display_date: nextDisplayDate,
        dismissed: false
      };
      localStorage.setItem(this.storageKey, JSON.stringify(newProps));
      if (this.timer) this.displayPromoTimer();
    }, (parseInt(this.secondsDelay) * 1000 + 3000));
  }

  displayPromoTimer() {
    this.minutesSpan = this.querySelector('.popup-modal__timer__minutes');
    this.secondsSpan = this.querySelector('.popup-modal__timer__seconds');
    this.totalSeconds = parseFloat(this.timerDuration) * 60;
    this.updateTimer();

    const timerInterval = setInterval(() => {
      this.totalSeconds -= 1;
      this.updateTimer();
      if (this.totalSeconds <= 0) {
        this.totalSeconds = (parseFloat(this.timerDuration) * 60) - 45;
      }
    }, 1000);
  }

  updateTimer() {
    let minutesValue = (Math.floor(this.totalSeconds / 60));
    if (minutesValue.toString().length === 1) minutesValue = '0' + minutesValue;
    let secondsValue = (this.totalSeconds % 60);
    if (secondsValue.toString().length === 1) secondsValue = '0' + secondsValue;
    this.minutesSpan.innerText = minutesValue;
    this.secondsSpan.innerText = secondsValue;
  }

  closeModal() {
    this.modal.classList.remove('popup-modal--active');
    this.overlay.classList.remove('popup-overlay--active');
  }
}
customElements.define('promo-popup', PromoPopup);

class SectionsGroup extends HTMLElement {
  constructor() {
    super();
    this.sectionOneContainer = this.querySelector('.section-group__section-one-container');
    this.sectionTwoContainer = this.querySelector('.section-group__section-two-container');

    this.transferSections();
    document.addEventListener("shopify:section:load", this.transferSections.bind(this));
  }

  transferSections() {
    this.sectionOne = document.querySelector(`${this.dataset.sectionOneId} .content-for-grouping`);
    this.sectionTwo = document.querySelector(`${this.dataset.sectionTwoId} .content-for-grouping`);

    if (this.sectionOne && !this.sectionOneContainer.childNodes.length) this.sectionOneContainer.appendChild(this.sectionOne);
    if (this.sectionTwo && !this.sectionTwoContainer.childNodes.length) this.sectionTwoContainer.appendChild(this.sectionTwo);
  }
}
customElements.define('section-group', SectionsGroup);

class ClickableDiscount extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.clickable-discount__btn');
    this.button.addEventListener('click', this.handleClick.bind(this));
    if (this.dataset.applied === 'true') {
      this.handleClick();
    } else {
      this.reapplyDiscountIfApplicable();
    }
  }

  handleClick() {
    this.dataset.loading = 'true';
    this.button.disabled = true;
    this.dataset.error = 'false';
    fetch(`/discount/${this.dataset.code}`)
      .then(response => {
        if (!response.ok) throw new Error('Error');
        this.dataset.applied = 'true';
        sessionStorage.setItem(`discount-${this.dataset.code}-applied`, "true");
      })
      .catch(error => {
        this.dataset.error = 'true';
        this.button.disabled = false;
      })
      .finally(() => {
        this.dataset.loading = 'false';
      })
  }

  reapplyDiscountIfApplicable() {
    const discountCode = this.dataset.code;
    if (sessionStorage.getItem(`discount-${discountCode}-applied`)) {
      this.dataset.applied = 'true';
      this.button.disabled = true;
      setTimeout(() => {
        fetch(`/discount/${discountCode}`)
          .catch(error => {
            this.dataset.applied = 'false';
            this.button.disabled = false;
          });
      }, 3000);
    }
  }
}
customElements.define('clickable-discount', ClickableDiscount);

class DynamicDates extends HTMLElement {
  constructor() {
    super();
    this.dateFormat = this.dataset.dateFormat;
    this.days = this.rearrangeDays(this.dataset.dayLabels.split(','));
    this.months = this.dataset.monthLabels.split(',');
    this.elementsToChange = this.querySelectorAll('[data-dynamic-date="true"]');

    this.insertDates();
    checkDateValidity(currentDate);
    document.addEventListener("shopify:section:load", (event) => {
      this.insertDates();
    });
  }

  insertDates() {
    this.elementsToChange.forEach(item => {
      const originalText = item.dataset.text;
      const minDays = parseInt(item.dataset.minDays);
      const maxDays = parseInt(item.dataset.maxDays);

      const startDate = addDays(currentDate, minDays);
      let startDateDaySuffix = 'th';
      const startDay = startDate.getDate();
      if (startDay === 1 || startDay === 21 || startDay === 31) startDateDaySuffix = 'st';
      else if (startDay === 2 || startDay === 22) startDateDaySuffix = 'nd';
      else if (startDay === 3 || startDay === 23) startDateDaySuffix = 'rd';
      
      const endDate = addDays(currentDate, maxDays);
      let endDateDaySuffix = 'th';
      const endDay = endDate.getDate();
      if (endDay === 1 || endDay === 21 || endDay === 31) endDateDaySuffix = 'st';
      else if (endDay === 2 || endDay === 22) endDateDaySuffix = 'nd';
      else if (endDay === 3 || endDay === 23) endDateDaySuffix = 'rd';

      let startDateString, endDateString;
      if (this.dateFormat === 'day_dd_mm') {
        startDateString = this.days[startDate.getDay()] + ', ' + startDate.getDate() + '. ' + this.months[startDate.getMonth()];
        endDateString = this.days[endDate.getDay()] + ', ' + endDate.getDate() + '. ' + this.months[endDate.getMonth()];
      } else if (this.dateFormat === 'mm_dd') {
        startDateString = this.months[startDate.getMonth()] + ' ' + startDate.getDate() + startDateDaySuffix;
        endDateString = this.months[endDate.getMonth()] + ' ' + endDate.getDate() + endDateDaySuffix;
      } else if (this.dateFormat === 'dd_mm') {
        startDateString = startDate.getDate() + '. ' + this.months[startDate.getMonth()];
        endDateString = endDate.getDate() + '. ' + this.months[endDate.getMonth()];
      } else if (this.dateFormat === 'dd_mm_no_dot') {
        startDateString = startDate.getDate() + ' ' + this.months[startDate.getMonth()];
        endDateString = endDate.getDate() + ' ' + this.months[endDate.getMonth()];
      } else if (this.dateFormat === 'day_dd_mm_numeric') {
        const startDateDay = String(startDate.getDate()).length > 1 ? startDate.getDate() : '0' + startDate.getDate();
        const startDateMonth = String((startDate.getMonth() + 1)).length > 1 ? (startDate.getMonth() + 1) : '0' + (startDate.getMonth() + 1);
        startDateString = this.days[startDate.getDay()] + ', ' + startDateDay + '. ' + startDateMonth + '.';
  
        const endDateDay = String(endDate.getDate()).length > 1 ? endDate.getDate() : '0' + endDate.getDate();
        const endDateMonth = String((endDate.getMonth() + 1)).length > 1 ? (endDate.getMonth() + 1) : '0' + (endDate.getMonth() + 1);
        endDateString = this.days[endDate.getDay()] + ', ' + endDateDay + '. ' + endDateMonth + '.';
      } else if (this.dateFormat === 'dd_mm_numeric') {
        const startDateDay = String(startDate.getDate()).length > 1 ? startDate.getDate() : '0' + startDate.getDate();
        const startDateMonth = String((startDate.getMonth() + 1)).length > 1 ? (startDate.getMonth() + 1) : '0' + (startDate.getMonth() + 1);
        startDateString = startDateDay + '. ' + startDateMonth + '.';
  
        const endDateDay = String(endDate.getDate()).length > 1 ? endDate.getDate() : '0' + endDate.getDate();
        const endDateMonth = String((endDate.getMonth() + 1)).length > 1 ? (endDate.getMonth() + 1) : '0' + (endDate.getMonth() + 1);
        endDateString = endDateDay + '. ' + endDateMonth + '.';
      } else {
        // day_mm_dd
        startDateString = this.days[startDate.getDay()] + ', ' + this.months[startDate.getMonth()] + ' ' + startDate.getDate() + startDateDaySuffix;
        endDateString = this.days[endDate.getDay()] + ', ' + this.months[endDate.getMonth()] + ' ' + endDate.getDate() + endDateDaySuffix;
      }

      const textWithStartDate = originalText.replace('[start_date]', startDateString);
      const finalText = textWithStartDate.replace('[end_date]', endDateString)
      item.innerHTML = finalText;
    })
  }

  rearrangeDays(arr) {
    arr.unshift(arr[6]);
    arr.length = 7;
    return arr;
  }
}
customElements.define('dynamic-dates', DynamicDates);

class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.isAfterScroll = this.dataset.afterScroll === 'true';
    this.isScrollBtn = this.dataset.scrollBtn === 'true';
    this.mainAtcBtn = document.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
    this.floatingBtns = document.querySelectorAll('.floating-btn')
    
    this.footerSpacing();
    if (this.isAfterScroll) {
      if (this.mainAtcBtn) {
        this.checkATCScroll();
        document.addEventListener('scroll', this.checkATCScroll.bind(this));
      }
    } else {
      this.floatingBtns.forEach(btn => {
        btn.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
      })
    }
    if (this.isScrollBtn) {
      this.scrollBtn = this.querySelector('.sticky-atc__scroll-btn');
      this.scrollDestination = document.querySelector(`${this.dataset.scrollDestination.replace('id', this.dataset.section)}`);
      if (this.scrollBtn && this.scrollDestination) this.scrollBtn.addEventListener('click', this.handleScrollBtn.bind(this));
    }
  }

  checkATCScroll() {
    if (window.scrollY > (this.mainAtcBtn.offsetTop + this.mainAtcBtn.offsetHeight)) {
      this.style.transform = 'none';
      this.scrolledPast = true;
    } else {
      this.style.transform = '';
      this.scrolledPast = false;
    }
    this.floatingBtns.forEach(btn => {
      if (this.scrolledPast) {
        btn.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
      } else {
        btn.style.setProperty('--sticky-atc-offset', '0px');
      }
    })
  }

  handleScrollBtn() {
    const stickyHeader = document.querySelector('sticky-header');
    const headerHeight = stickyHeader ? stickyHeader.clientHeight : 0;
    window.scrollTo({
      top: this.scrollDestination.offsetTop - headerHeight - 15,
      behavior: 'smooth'
    })
  }

  footerSpacing() {
    let spacerDiv = document.querySelector('.sticky-atc-footer-spacer');
    if (spacerDiv) return;

    spacerDiv = document.createElement('div');
    spacerDiv.className = 'sticky-atc-footer-spacer';
    spacerDiv.style.height = (this.clientHeight -1) + 'px';
    spacerDiv.style.display = 'block';
    const footer = document.querySelector('.footer');
    if (footer) spacerDiv.style.background = window.getComputedStyle(footer).background;
    document.body.appendChild(spacerDiv);
    // if (footer) footer.style.marginBottom = (this.clientHeight -1) + 'px';
    // document.body.style.paddingBottom = (this.clientHeight -1) + 'px';
  }
}
customElements.define('sticky-atc', StickyAtc);

class BundleDeals extends HTMLElement {
  constructor() {
    super();
    this.productContainers = this.querySelectorAll('.bundle-deals__product-js');
    this.mediaItemContainers = this.querySelectorAll('.bundle-deals__media-item-container-js');
    this.mediaItemImgs = this.querySelectorAll('.bundle-deals__media-item-img-js');
    this.checkboxes = this.querySelectorAll('.bundle-deals__checkbox-js');
    this.variantPickers = this.querySelectorAll('.bundle-deals__variant-selects-js');
    this.skipNonExistent = this.dataset.skipNonExistent === 'true';
    this.skipUnavailable = this.dataset.skipUnavailable === 'true';
    this.prices = this.querySelectorAll('.bundle-deals__price-js');
    this.comparePrices = this.querySelectorAll('.bundle-deals__compare-price-js');
    this.totalPrice = this.querySelector('.bundle-deals__total-price-js');
    this.totalComparePrice = this.querySelector('.bundle-deals__total-compare-price-js');
    this.updatePrices = this.dataset.updatePrices === 'true';
    this.percentageLeft = parseFloat(this.dataset.percentageLeft);
    this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
    this.currencySymbol = this.dataset.currencySymbol;

    this.selectedVariants = {
      id_1: null,
      id_2: null,
      id_3: null,
      id_4: null,
      id_5: null,
    };
    this.formVariants = [];

    this.initIds();
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    })
    this.variantPickers.forEach(select => {
      select.addEventListener('change', this.handleSelectChange.bind(this));
    })
  }

  initIds() {
    this.checkboxes.forEach(checkbox => {
      this.selectedVariants[checkbox.dataset.idIndex] = {
        id: checkbox.dataset.id,
        price: checkbox.dataset.price,
        comparePrice: checkbox.dataset.comparePrice,
        checked: true
      }
    });
    this.updateFormIds();
  }

  handleCheckboxChange(e) {
    const checkbox = e.currentTarget;
    const checked = checkbox.checked;
    const index = parseInt(checkbox.dataset.index);
    this.selectedVariants[checkbox.dataset.idIndex].checked = checked;
    const productContainer = this.productContainers[index];
    const itemSelects = productContainer.querySelectorAll('select');
    if (checked) {
      this.mediaItemContainers[index].classList.remove('bundle-deals__media-item--disabled');
      productContainer.classList.remove('bundle-deals__product--deselected');
      itemSelects.forEach(select => {
        select.removeAttribute('disabled');
      })
    } else {
      this.mediaItemContainers[index].classList.add('bundle-deals__media-item--disabled');
      productContainer.classList.add('bundle-deals__product--deselected');
      itemSelects.forEach(select => {
        select.setAttribute('disabled', '');
      })
    }
    this.updateFormIds();
    if (this.updatePrices) this.updateTotalPrice();
  }

  handleSelectChange(e) {
    const select = e.currentTarget;
    const index = parseInt(select.dataset.index);
    const selectedOptions = Array.from(select.querySelectorAll('select'), (select) => select.value);

    // update the "unavailable" text in option labels and adjust the "non-existent" & "unavailable" classes
    const selectedOptionOneVariants = JSON.parse(select.querySelector('[type="application/json"]').textContent).filter(
      (variant) => select.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...select.querySelectorAll('.select--small')];
    updateVariantStatuses(selectedOptionOneVariants, inputWrappers);
    // find selected variant JSON
    const selectedVariant = JSON.parse(select.querySelector('[type="application/json"]').textContent).find((variant) => {
      return !variant.options.map((option, index) => {
        return selectedOptions[index] === option;
      }).includes(false);
    });
    if (!selectedVariant) {
      // try to find next or previous available variant
      if (this.skipNonExistent) findAvailableVariant(select, selectedOptions, false, true, this.skipUnavailable);
      return;
    }
    if (this.skipUnavailable && !selectedVariant.available) {
      // new available variant found, prevent UI chnages of old variant
      if (findAvailableVariant(select, selectedOptions, false, true, true)) return;
    }
    let { price, compare_at_price, featured_image } = selectedVariant;
    price = parseInt(price);
    let comparePrice = compare_at_price ? parseInt(compare_at_price) : price;
    const percentageLeft = select.dataset.percentageLeft ?? 1;
    const fixedDiscount = select.dataset.fixedDiscount ?? 0;
    price = price * percentageLeft - fixedDiscount;
    if (featured_image) featured_image = featured_image.src;
    const idToAdd = selectedVariant.id;

    this.selectedVariants[select.dataset.idIndex].id = idToAdd;
    this.selectedVariants[select.dataset.idIndex].price = price;
    this.selectedVariants[select.dataset.idIndex].comparePrice = comparePrice;
    this.updateFormIds();
    if (this.updatePrices) {
      this.prices[index].innerHTML = this.currencySymbol + ((price / 100).toFixed(2));
      if (comparePrice > price) {
        this.comparePrices[index].innerHTML = this.currencySymbol + ((comparePrice / 100).toFixed(2));
      } else {
        this.comparePrices[index].innerHTML = '';
      }
      this.updateTotalPrice();
    } 
    if (featured_image && featured_image.length > 0 && this.mediaItemImgs[index]) {
      this.mediaItemImgs[index].src = featured_image;
    }
  }

  updateFormIds() {
    const formArray = [];
    const keysToAdd = this.selectedVariants;
    for (const key in keysToAdd) {
      const current = keysToAdd[key];
      if (current != null && current.checked) {
        const itemIndex = formArray.findIndex(elem => elem.id === current.id);
        if (itemIndex < 0) {
          formArray.unshift({
           id: current.id,
           quantity: 1,
          });
        } else {
          formArray[itemIndex].quantity += 1;
        }
      }
    }
    this.formVariants = formArray;
  }
  
  updateTotalPrice() {
    const prices = [];
    const comparePrices = [];
    const keys = this.selectedVariants;
    for (const key in keys) {
      const current = keys[key];
      if (current != null && current.checked) {
        prices.push(parseInt(current.price));
        comparePrices.push(parseInt(current.comparePrice));
      }
    }
    const price = prices.reduce((a, b) => a + b, 0);
    const discountedPrice = price * this.percentageLeft - this.fixedDiscount;
    const comparePrice = comparePrices.reduce((a, b) => a + b, 0);
    this.totalPrice.innerHTML = this.currencySymbol + ((discountedPrice / 100).toFixed(2));
    if (comparePrice > discountedPrice) {
      this.totalComparePrice.innerHTML = this.currencySymbol + ((comparePrice / 100).toFixed(2));
    } else {
      this.totalComparePrice.innerHTML = '';
    }
  }
}
customElements.define('bundle-deals', BundleDeals);

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();
    this.quantityGifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);
    this.inputs = this.querySelectorAll('input[name="quantity"]');
    this.labels = this.querySelectorAll('.quantity-break');
    this.jsonData = this.querySelector('[type="application/json"]');
    this.hasVariants = this.jsonData.dataset.hasVariants === 'true';
    this.selectedVariants = {
      input_1: [],
      input_2: [],
      input_3: [],
      input_4: [],
      input_5: [],
      input_6: [],
    };
    this.updateUnavailable = this.dataset.updateUnavailable === 'true';
    this.skipNonExistent = this.dataset.skipNonExistent === 'true';
    this.skipUnavailable = this.dataset.skipUnavailable === 'true';
    this.formVariants = [];
    this.selectedQuantity = 1;
    if (this.querySelector('input[checked]')) this.selectedQuantity = parseInt(this.querySelector('input[checked]').value);
    this.variantSelects = this.querySelectorAll('.quantity-break__selector-item');
    this.updatePrices = this.dataset.updatePrices === 'true';
    this.moneyFormat = this.dataset.moneyFormat;

    if (this.hasVariants) this.initVariants();
    this.inputs.forEach(input => {
      input.addEventListener('change', this.handleChange.bind(this));
    })
    this.variantSelects.forEach(select => {
      select.addEventListener('change', this.handleSelectChange.bind(this));
    })
  }

  handleSelectChange(e) {
    const currentSelect = e.currentTarget;
    const selectedOptions = Array.from(currentSelect.querySelectorAll('select'), (select) => select.value);

    // update the "unavailable" text in option labels and adjust the "non-existent" & "unavailable" classes
    if (this.updateUnavailable) {
      const selectedOptionOneVariants = this.getVariantData().filter(
        (variant) => currentSelect.querySelector(':checked').value === variant.option1
      );
      const inputWrappers = [...currentSelect.querySelectorAll('.select--small')];
      updateVariantStatuses(selectedOptionOneVariants, inputWrappers);
    }
    // find selected variant JSON
    const selectedVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return selectedOptions[index] === option;
      }).includes(false);
    });
    if (!selectedVariant) {
      // try to find next or previous available variant
      if (this.skipNonExistent) findAvailableVariant(currentSelect, selectedOptions, false, true, this.skipUnavailable);
      return;
    }
    if (this.skipUnavailable && !selectedVariant.available) {
      // new available variant found, prevent UI chnages of old variant
      if (findAvailableVariant(currentSelect, selectedOptions, false, true, true)) return;
    }
    currentSelect.dataset.selectedId = selectedVariant.id;
    const selectIndex = currentSelect.dataset.selectIndex;
    const selectedLabel = currentSelect.closest('.quantity-break');
    const selectInput = selectedLabel.dataset.input;

    this.selectedVariants[selectInput][selectIndex] = selectedVariant.id;
    this.formVariants = this.selectedVariants[selectInput];
    this.updateMedia(selectedVariant);
    if (!this.updatePrices) return;
    var totalPrice = 0;
    var totalComparePrice = 0;
    const quantity = parseFloat(selectedLabel.dataset.quantity);
    const percentageLeft = parseFloat(selectedLabel.dataset.percentageLeft);
    const fixedDiscount = parseFloat(selectedLabel.dataset.fixedDiscount);
    for (let i=0; i<quantity; i++) {
      const currentId = parseInt(this.selectedVariants[selectInput][i]);
      const currentVariant = this.getVariantData().find(v => parseInt(v.id) === currentId);
      if (!currentVariant) return;
      totalPrice += currentVariant.price;
      if (currentVariant.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
        totalComparePrice += currentVariant.compare_at_price
      } else {
        totalComparePrice += currentVariant.price;
      }
    }
    totalPrice = totalPrice * percentageLeft - fixedDiscount;
    const priceDifference = totalComparePrice - totalPrice;
    const priceDifferenceRounded = Math.round(priceDifference / 100) * 100;
    const priceEach = totalPrice / quantity;
    const comparePriceEach = totalComparePrice / quantity;

    const formattedTotalPrice = formatMoney(totalPrice, this.moneyFormat, true);
    const formattedTotalComparePrice = formatMoney(totalComparePrice, this.moneyFormat, true);
    const formattedPriceDifference = formatMoney(priceDifference, this.moneyFormat, true);
    const formattedPriceDifferenceRounded = formatMoney(priceDifferenceRounded, this.moneyFormat, true);
    const formattedPriceEach = formatMoney(priceEach, this.moneyFormat, true);
    const formattedComparePriceEach = formatMoney(comparePriceEach, this.moneyFormat, true);

    selectedLabel.querySelectorAll('.variant-price-update').forEach(textElement => {
      let textValue = textElement.dataset.text;

      textValue = textValue.replace('[quantity]', quantity);
      textValue = textValue.replace('[price]', formattedTotalPrice);
      textValue = textValue.replace('[compare_price]', formattedTotalComparePrice);
      textValue = textValue.replace('[amount_saved]', formattedPriceDifference);
      textValue = textValue.replace('[amount_saved_rounded]', formattedPriceDifferenceRounded);
      textValue = textValue.replace('[price_each]', formattedPriceEach);
      textValue = textValue.replace('[compare_price_each]', formattedComparePriceEach);

      textElement.innerHTML = textValue;
    });
    const comparePriceElement = selectedLabel.querySelector('.quantity-break__compare-price');
    if (comparePriceElement) {
      if (totalComparePrice > totalPrice) {
        comparePriceElement.classList.remove('hidden');
      } else {
        comparePriceElement.classList.add('hidden');
      }
    }
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.jsonData.textContent);
    return this.variantData;
  }

  initVariants() {
    if (!this.hasVariants) return;
    this.labels.forEach(label => {
      if (label.querySelector('.quantity-break__variants')) {
        let array = [];
        label.querySelectorAll('.quantity-break__selector-item').forEach(select => {
          array.push(select.dataset.selectedId);
        })
        this.selectedVariants[label.dataset.input] = array;
      }
    })
    this.formVariants = [];
  }

  updateMedia(selectedVariant) {
    if (!selectedVariant) return;
    if (!selectedVariant.featured_media) return;
    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach(mediaGallery => mediaGallery.setActiveMedia(`${this.dataset.section}-${selectedVariant.featured_media.id}`, true));
  }
  
  handleChange(e) {
    const value = parseInt(e.target.value);
    this.selectedQuantity = value;
    
    if (this.hasVariants) this.formVariants = this.selectedVariants[e.target.dataset.input];
    if (this.quantityGifts) this.quantityGifts.unlockGifts(value);
  }
}
customElements.define('quantity-breaks', QuantityBreaks);

function updateVariantStatuses(selectedOptionOneVariants, inputWrappers) {
  inputWrappers.forEach((option, index) => {
    if (index === 0) return;
    const optionInputs = [...option.querySelectorAll('option')];
    const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
    const availableOptionInputsValue = selectedOptionOneVariants
      .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected)
      .map(variantOption => variantOption[`option${index + 1}`]);
    const existentOptionInputsValue = selectedOptionOneVariants
      .filter(variant => variant[`option${index}`] === previousOptionSelected)
      .map(variantOption => variantOption[`option${index + 1}`]);


    optionInputs.forEach(input => {
      input.classList.remove('unavailable', 'non-existent');
      if (existentOptionInputsValue.includes(input.getAttribute('value'))) {
        if (availableOptionInputsValue.includes(input.getAttribute('value'))) {
          input.innerText = input.getAttribute('value');
        } else {
          input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
          input.classList.add('unavailable');
        }
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
        input.classList.add('non-existent');
      }
    });
  });
}

function findAvailableVariant(container, options, isMain = true, skipNonExistent = false, skipUnavailable = false) {

  for (let i = options.length - 1; i >= 0; i--) {
    const inputWrapper = isMain
      ? container.querySelector(`.product-form__input:nth-child(${i + 1})`)
      : container.querySelectorAll('.select')[i];

    const type = isMain
      ? inputWrapper.querySelector('.product-form__input__type').dataset.type
      : 'dropdown'; // Assuming select for quantity breaks

    const optionElements = type === 'dropdown'
      ? inputWrapper.querySelectorAll('option')
      : inputWrapper.querySelectorAll('input[type="radio"]');

    // Find the index of the current option value
    let currentIndex = Array.from(optionElements).findIndex(option => option.value === options[i]);

    // Try to find the next available option in the current set
    let found = false;
    while (currentIndex < optionElements.length - 1) {
      currentIndex++;
      const nextOption = optionElements[currentIndex];

      if ((!skipNonExistent || !nextOption.classList.contains('non-existent')) &&
          (!skipUnavailable || !nextOption.classList.contains('unavailable'))) {
        // Update the option in options array
        options[i] = nextOption.value;
        found = true;
        break;
      }
    }

    // If no next available option found, try to find the previous one
    if (!found) {
      currentIndex = Array.from(optionElements).findIndex(option => option.value === options[i]);
      while (currentIndex > 0) {
        currentIndex--;
        const prevOption = optionElements[currentIndex];

        if ((!skipNonExistent || !prevOption.classList.contains('non-existent')) &&
            (!skipUnavailable || !prevOption.classList.contains('unavailable'))) {
          // Update the option in options array
          options[i] = prevOption.value;
          found = true;
          break;
        }
      }
    }

    // If a valid option is found, update the corresponding input and trigger change
    if (found) {
      if (type === 'dropdown') {
        inputWrapper.querySelector('.select__select').value = options[i];
      } else {
        inputWrapper.querySelector(`input[type="radio"][value="${options[i]}"]`).checked = true;
      }

      // Trigger a change event for the updated input
      container.dispatchEvent(new Event('change'));
      return true; // Return true if the UI should be stopped from further updates
    }
  }
  return false; // Return false if no valid option was found and UI can continue to update
}

class QuantityGifts extends HTMLElement {
  constructor() {
    super();
    this.gifts = this.querySelectorAll('.quantity-gift');
    this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
    this.quantitySelector = document.getElementById(`Quantity-Form--${this.dataset.section}`);
    this.unlockedItems = [];
    this.mainProduct = this.dataset.mainProduct;
    this.cartItems = document.querySelector('cart-drawer-items');
    
    this.initUnlock();
  }

  initUnlock() {
    let initQuantity = 1;
    const mainProductCartItems = this.mainProduct ? document.querySelectorAll(`.cart-item--product-${this.mainProduct}`) : null;
    if (mainProductCartItems && mainProductCartItems.length) {
      initQuantity = 0;
      for (let i=0; i < mainProductCartItems.length; i++) {
        initQuantity += parseInt(mainProductCartItems[i].dataset.quantity);
      }
    } else if (this.quantityBreaks) {
      initQuantity = parseInt(this.quantityBreaks.selectedQuantity);
    } else if (this.quantitySelector) {
      const input = this.quantitySelector.querySelector('input[name="quantity"]')
      initQuantity = parseInt(input.value);
    }
    this.unlockGifts(initQuantity, true);
  }

  unlockGifts(quantity, addRemoveProducts = false) {
    this.unlockedItems = [];
    this.gifts.forEach(gift => {
      if (parseInt(gift.dataset.quantity) <= quantity) {
        gift.classList.add('quantity-gift--unlocked');
        gift.dataset.unlocked = 'true';
        const cartItem = document.querySelector(`.cart-item--product-${gift.dataset.handle}`);
        if (gift.dataset.product && !cartItem) this.unlockedItems.unshift(gift.dataset.product);
      } else {
        gift.classList.remove('quantity-gift--unlocked');
        gift.dataset.unlocked = 'false';
      }
    })
    if (this.mainProduct && addRemoveProducts) this.addRemoveProducts();
  }

  addRemoveProducts() {
    if (!this.mainProduct) return;
    const mainProductCartItems = document.querySelectorAll(`.cart-item--product-${this.mainProduct}`);
    this.gifts.forEach(gift => {
      if (mainProductCartItems.length) {
        if (gift.dataset.unlocked === 'true' && gift.dataset.product) {
          this.addToCart(gift);
        } else {
          this.removeFromCart(gift);
        }
      } else {
        this.removeFromCart(gift);
      }
    })
  }

  addToCart(item) {
    const cartItem = document.querySelector(`.cart-item--product-${item.dataset.handle}`);
    if (cartItem) return;
    const form = item.querySelector('product-form');
    if (form) form.handleSubmit();
  }

  removeFromCart(item) {
    const cartItem = document.querySelector(`.cart-item--product-${item.dataset.handle}`);
    if (!cartItem || !this.cartItems) return;
    this.cartItems.updateQuantity(cartItem.dataset.index, 0);
  }
}
customElements.define('quantity-gifts', QuantityGifts);

class ProductInfoUpsell extends HTMLElement {
  constructor() {
    super();
    this.image = this.querySelector('.upsell__image__img');
    this.toggleBtn = this.querySelector('.upsell-toggle-btn');
    this.variantSelects = this.querySelector('.upsell__variant-picker');
    this.variantSelectElements = this.querySelectorAll('.select__select');
    this.jsonData = this.querySelector('[type="application/json"]');
    this.productForm = this.querySelector('product-form');
    if (this.productForm) this.idInput = this.productForm.querySelector('[name="id"]');
    this.skipNonExistent = this.dataset.skipNonExistent === 'true';
    this.skipUnavailable = this.dataset.skipUnavailable === 'true';
    this.updatePrices = this.dataset.updatePrices === 'true';
    if (this.updatePrices) {
      this.price = parseInt(this.dataset.price);
      this.comparePrice = parseInt(this.dataset.comparePrice);
      this.priceSpan = this.querySelector('.upsell__price .regular-price');
      this.comparePriceSpan = this.querySelector('.upsell__price .compare-price');
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
      this.moneyFormat = this.dataset.moneyFormat;
      this.isMainOfferItem = this.dataset.mainOfferItem === 'true';
      if (this.isMainOfferItem) this.mainOfferContainer = document.querySelector(`#MainBundleOffer-${this.dataset.section}`);
    }

    if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.handleToggle.bind(this));
    if (this.variantSelects) this.variantSelects.addEventListener('change', this.handleSelectChange.bind(this));
  }

  handleToggle(e) {
    if (e.target.nodeName.toLowerCase() === 'select' || e.target.nodeName.toLowerCase()  === 'option') return;
    if (this.dataset.selected === 'true') {
      this.dataset.selected = 'false';
    } else {
      this.dataset.selected = 'true';
    }
  }

  handleSelectChange(e) {
    const currentSelect = e.currentTarget;
    const selectedOptions = Array.from(currentSelect.querySelectorAll('select'), (select) => select.value);
    const selectedVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return selectedOptions[index] === option;
      }).includes(false);
    });
    // new
    const selectedOptionOneVariants = this.getVariantData().filter(
      (variant) => currentSelect.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...currentSelect.querySelectorAll('select')];
    updateVariantStatuses(selectedOptionOneVariants, inputWrappers);
    if (!selectedVariant) {
      // try to find next or previous available variant
      if (this.skipNonExistent) findAvailableVariant(currentSelect, selectedOptions, false, true, this.skipUnavailable);
      return;
    }
    if (this.skipUnavailable && !selectedVariant.available) {
      // new available variant found, prevent UI chnages of old variant
      if (findAvailableVariant(currentSelect, selectedOptions, false, true, true)) return;
    }
    // end new
    if (this.updatePrices) {
      this.price = selectedVariant.price * this.percentageLeft - this.fixedDiscount;
      this.comparePrice = selectedVariant.price;
      if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
        this.comparePrice = selectedVariant.compare_at_price;
      }
      displayPrices(this.price, this.comparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
    }
    if (this.image && selectedVariant.featured_image) this.image.src = selectedVariant.featured_image.src;
    this.updateId(selectedVariant.id);
    if (this.isMainOfferItem && this.mainOfferContainer.updateTotalPrices) this.mainOfferContainer.updateTotalPrices();
  }

  updateId(id) {
    this.dataset.id = id;
    if (this.idInput) this.idInput.value = id;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.jsonData.textContent);
    return this.variantData;
  }
}
customElements.define('product-info-upsell', ProductInfoUpsell);

class CartDrawerUpsell extends ProductInfoUpsell {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = this.cartDrawer.querySelector('cart-drawer-items');
    this.classicAddButton = this.querySelector('.upsell__add-btn');
  }

  handleToggle(e) {
    if (e.target.nodeName.toLowerCase() === 'select' || e.target.nodeName.toLowerCase()  === 'option') return;
    if (this.dataset.selected === 'true') {
      this.dataset.selected = 'false';
      this.removeFromCart();
    } else {
      this.dataset.selected = 'true';
      this.addToCart();
    }
  }

  addRemoveFromCart() {
    if (this.dataset.selected === 'true' && !this.cartDrawer.classList.contains('is-empty')) {
      this.addToCart();
    } else {
      this.removeFromCart();
    }
  }

  addToCart() {
    const cartItem = this.cartDrawer.querySelector(`.cart-item--product-${this.dataset.handle}`);
    if (cartItem) return;
    if (this.toggleBtn) this.toggleBtn.setAttribute('disabled', '');
    this.variantSelectElements.forEach(select => {
      select.setAttribute('disabled', '');
    })
    this.productForm.handleSubmit();
  }

  removeFromCart() {
    const cartItem = this.cartDrawer.querySelector(`.cart-item--product-${this.dataset.handle}`);
    if (!cartItem || !this.cartItems) return;
    if (this.toggleBtn) this.toggleBtn.setAttribute('disabled', '');
    this.variantSelectElements.forEach(select => {
      select.setAttribute('disabled', '');
    })
    this.cartItems.updateQuantity(cartItem.dataset.index, 0);
  }

  updateId(id) {
  this.dataset.id = id;
  this.idInput.value = id;
  
  if (this.classicAddButton) return;
  
  if (this.dataset.selected === 'true') {
    this.removeFromCart();
    
    const addToCartListener = (event) => {
      if (event.detail.handle === 'handle') {
        this.addToCart();
        document.removeEventListener('cartQuantityUpdated', addToCartListener);
      }
    };
    
    document.addEventListener('cartQuantityUpdated', addToCartListener);
  }
}
  
  getUpdateRequired() {
    const cartItem = this.cartDrawer.querySelector(`.cart-item--product-${this.dataset.handle}`);
    let updateRequired = false;
    if (cartItem && this.dataset.selected === 'false') {
      // remove required
      updateRequired = true;
    } else if (!cartItem && this.dataset.selected === 'true') {
      // add required
      updateRequired = true;
    }
    return updateRequired;
  }
}
customElements.define('cart-drawer-upsell', CartDrawerUpsell);

function displayPrices(price, comparePrice, priceSpan, comparePriceSpan, moneyFormat) {
  if (!moneyFormat) return;

  if (price && priceSpan) {
    var priceHTML = formatMoney(price, moneyFormat);
    priceSpan.innerHTML = priceHTML;
  }

  if (comparePrice && comparePriceSpan) {
    var comparePriceHTML = formatMoney(comparePrice, moneyFormat);
    comparePriceSpan.innerHTML = comparePriceHTML;

    if (comparePrice > price) {
      comparePriceSpan.classList.remove('hidden');
    } else {
      comparePriceSpan.classList.add('hidden');
    }
  }
}

function formatMoney(cents, format, removeTrailingZeros = false) {
  if (typeof cents == 'string') { cents = cents.replace('.', ''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format;

  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) { return 0; }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents = parts[1] ? (decimal + parts[1]) : '';

    // money_without_trailing_zeros if removeTrailingZeros
    if (removeTrailingZeros && cents === decimal + '00') {
      cents = '';
    }

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

class CartDrawerGift extends CartDrawerUpsell {
  constructor() {
    super();
  }
}
customElements.define('cart-drawer-gift', CartDrawerGift);

function initToggleUpsells() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach(upsell => {
      if (upsell.addRemoveFromCart) upsell.addRemoveFromCart();
    })
  }
}
initToggleUpsells();

class MainBundleOffer extends HTMLElement {
  constructor() {
    super();
    this.offerItems = this.querySelectorAll('.main-offer-item');
    this.updatePrices = this.dataset.updatePrices === 'true';
    if (this.updatePrices) {
      this.priceSpan = this.querySelector('.bundle-deals__total-price-js');
      this.comparePriceSpan = this.querySelector('.bundle-deals__total-compare-price-js');
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
      this.moneyFormat = this.dataset.moneyFormat;
    }
  }

  updateTotalPrices() {
    if (!this.updatePrices) return;
    var totalPrice = 0;
    var totalComparePrice = 0;
    for (let i=0; i<this.offerItems.length; i++) {
      totalPrice += parseInt(this.offerItems[i].price);
      totalComparePrice += parseInt(this.offerItems[i].comparePrice);
    }
    totalPrice = totalPrice * this.percentageLeft - this.fixedDiscount;
    displayPrices(totalPrice, totalComparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
  }
}
customElements.define('main-bundle-offer', MainBundleOffer);

class CustomProductField extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    this.fieldName = this.dataset.name;
    this.input = this.querySelector('[type="text"], [type="number"], textarea');
    this.inputRadios = this.querySelectorAll('[type="radio"]');
    this.select = this.querySelector('.select__select');
    this.productForm = document.getElementById(`product-form-${this.dataset.section}`);
    this.prevValue = this.dataset.defaultValue;
    this.isRequired = this.dataset.required === 'true';
    this.isText = true;
    this.notMain = this.dataset.notMain === 'true';
    if (this.dataset.type === 'select' || this.dataset.type === 'pills') this.isText = false;
  
    this.createInputs();
    if (this.isRequired && this.isText) {
      this.isValid = true;
      this.atcButtons = this.notMain ? document.querySelectorAll(`.featured-product-atc-${this.dataset.section}`) : document.querySelectorAll('.main-product-atc');
      this.mainAtcButton = this.productForm.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
      this.mainAtcBtnLabel = this.mainAtcButton.querySelector('.main-atc__label');
      this.mainAtcBtnError = this.mainAtcButton.querySelector('.main-atc__error');
      this.atcErrorMsg = this.dataset.atcErrorMsg;
      this.mainAtcButton.dataset.requiredFields = parseInt(this.mainAtcButton.dataset.requiredFields) + 1;
      this.mainAtcBtnError.innerHTML = this.atcErrorMsg;
      this.applyStickyAtcError = this.dataset.applyStickyAtcError === 'true';
      this.stickyAtcButton = document.querySelector(`#sticky-atc-${this.dataset.section}`);
      if (this.applyStickyAtcError && this.stickyAtcButton) {
        this.stickyAtcBtnLabel = this.stickyAtcButton.querySelector('.sticky-atc__label');
        this.stickyAtcBtnError = this.stickyAtcButton.querySelector('.sticky-atc__error');
        this.stickyAtcBtnError.innerHTML = this.atcErrorMsg;
      }
      this.validateValue(this.prevValue, null);
    } 
    if (this.input) this.input.addEventListener('input', this.handleChange.bind(this));
    this.inputRadios.forEach(input => {
      input.addEventListener('input', this.handleChange.bind(this));
    })
    if (this.select) this.select.addEventListener('change', this.handleChange.bind(this));
  }

  handleChange(event) {
    const value = event.target.value.trim();
    if (event.target.checkValidity()) {
      this.prevValue = value;
    } else {
      event.target.value = this.prevValue;
      return;
    }
    this.dataset.value = value;
    this.productFormInput.value =  value;
    
    if (this.isRequired && this.isText) this.validateValue(value, event.target);
  }

  validateValue(value, input) {
    const isValid = value.length > 0 ? true : false;
    if (isValid === this.isValid) return;
    this.isValid = isValid;
    if (input) {
      if (this.isValid) {
        input.classList.remove('input--error');
        this.mainAtcButton.dataset.validFields = parseInt(this.mainAtcButton.dataset.validFields) + 1;
      } else {
        input.classList.add('input--error');
        this.mainAtcButton.dataset.validFields = parseInt(this.mainAtcButton.dataset.validFields) - 1;
      }
    }
    const buttonValid = this.mainAtcButton.dataset.validFields === this.mainAtcButton.dataset.requiredFields;
    const variantUnavailable = this.mainAtcButton.dataset.unavailable === 'true';
    this.atcButtons.forEach(btn => {
      if (buttonValid && !variantUnavailable) {
        btn.removeAttribute('disabled');
      } else {
        btn.setAttribute('disabled', '');
      }
    })
    if (this.atcErrorMsg.length === 0) return;
    if (buttonValid) {
      this.mainAtcBtnLabel.style.display = '';
      this.mainAtcBtnError.style.display = 'none';
      if (this.applyStickyAtcError && this.stickyAtcButton) {
        this.stickyAtcBtnLabel.style.display = '';
        this.stickyAtcBtnError.style.display = 'none';
      }
    } else {
      this.mainAtcBtnLabel.style.display = 'none';
      this.mainAtcBtnError.style.display = '';
      if (this.applyStickyAtcError && this.stickyAtcButton) {
        this.stickyAtcBtnLabel.style.display = 'none';
        this.stickyAtcBtnError.style.display = '';
      }
    }
  }

  createInputs() {
    this.productFormInput = document.createElement('input');
    this.productFormInput.setAttribute('type', 'hidden');
    this.productFormInput.setAttribute('name', `properties[${this.fieldName}]`);
    this.productFormInput.value =  this.dataset.defaultValue;
    this.productFormInput.classList.add('arcane-property-input');
    this.productForm.appendChild(this.productFormInput);
  }
}
customElements.define('custom-product-field', CustomProductField);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.secondarySelectSelector = 'StickyAtcVariantPicker-';
    this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
    this.isSecondary = false;
    this.QuantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
    this.hasQuantityBreaksPicker = this.dataset.hasQuantityBreaksPicker === 'true';
    this.prependMedia = this.dataset.disablePrepend != 'true';
    this.filtering = this.dataset.hasFiltering === 'true';
    this.skipNonExistent = this.dataset.skipNonExistent === 'true';
    this.skipUnavailable = this.dataset.skipUnavailable === 'true';
    if (this.hasQuantityBreaksPicker) {
      this.quantityBreaksPickerStyle = this.dataset.quantityBreaksPickerStyle;
      this.quantityBreaksPickerDisplayedImages = this.dataset.quantityBreaksPickerDisplayedImages;
    }
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();
    
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      if (this.skipNonExistent && findAvailableVariant(this, this.options, true, true, this.skipUnavailable)) return;
      this.setUnavailable();
    } else {
      if (this.skipUnavailable && !this.currentVariant.available) {
        if (findAvailableVariant(this, this.options, true, true, true)) return;
      }
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    const optionsArr = [];
    this.querySelectorAll('.product-form__input').forEach(input => {
      let option;
      const type = input.querySelector('.product-form__input__type').dataset.type;
      if (type == 'dropdown' || type == 'dropdwon') {
        option = input.querySelector('.select__select').value;
      } else {
        option = input.querySelector('input[type="radio"]:checked').value;
      }
      optionsArr.push(option);
    })
    this.options = optionsArr;
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach(mediaGallery => mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.prependMedia, this.filtering, this.currentVariant));

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector( `[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    let inputWrappers;
    if (this.isSecondary && this.secondarySelect) {
      inputWrappers = [...this.secondarySelect.querySelectorAll('.product-form__input')];
    } else {
      inputWrappers = [...this.querySelectorAll('.product-form__input')];
    }
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], .select__select option')]
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map(variantOption => variantOption[`option${index + 1}`]);
      const existentOptionInputsValue = selectedOptionOneVariants
        .filter(variant => variant[`option${index}`] === previousOptionSelected)
        .map(variantOption => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue, existentOptionInputsValue)
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions, existentOptionInputsValue) {
    listOfOptions.forEach(input => {
      input.classList.remove('unavailable', 'non-existent', 'disabled');
      const value = input.getAttribute('value');
      let swatchInput = null;
      if (input.dataset.swatch === 'true') {
        swatchInput = input.closest('.color-swatch');
      }
      if (swatchInput) swatchInput.classList.remove('unavailable', 'non-existent', 'disabled');
      if (existentOptionInputsValue.includes(value)) {
        if (listOfAvailableOptions.includes(value)) {
          // available
          if (input.nodeName === 'OPTION') {
            input.innerText = value;
          } else {
            input.classList.remove('disabled');
            if (swatchInput) swatchInput.classList.remove('disabled');
          }
        } else {
          // unavailable but existent
          if (input.nodeName === 'OPTION') {
            input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
            input.classList.add('unavailable');
          } else {
            input.classList.add('disabled', 'unavailable');
            if (swatchInput) swatchInput.classList.add('disabled', 'unavailable');
          }
        }
      } else {
        // non-existent
        if (input.nodeName === 'OPTION') {
          input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
          input.classList.add('non-existent');
        } else {
          input.classList.add('disabled', 'non-existent');
          if (swatchInput) swatchInput.classList.add('disabled', 'non-existent');
        }
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      if (pickUpAvailability.fetchAvailability) pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`price-${this.dataset.section}`);
        // main price
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);

        // sticky atc
        const stickyAtcSeparatePriceDestination = document.getElementById(`sticky-atc-separate-price-${this.dataset.section}`);
        const stickyAtcSeparatePriceSource = html.getElementById(`sticky-atc-separate-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        // sticky atc btn price
        const stickyAtcPriceDestination = document.getElementById(`sticky-atc-price-${this.dataset.section}`);
        const stickyAtcPriceSource = html.getElementById(`sticky-atc-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        // sticky atc image
        const stickyAtcImageDestination = document.getElementById(`sticky-atc-image-${this.dataset.section}`);
        const stickyAtcImageSource = html.getElementById(`sticky-atc-image-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);

        // main atc
        const mainAtcPriceDestination = document.getElementById(`main-atc-price-${this.dataset.section}`);
        const mainAtcPriceSource = html.getElementById(`main-atc-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);

        // variant custom labels
        const customLabelsDestination = document.querySelectorAll(`[id^="custom-label-${this.dataset.section}"]`);
        const customLabelsSource = html.querySelectorAll(`[id^="custom-label-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}"]`);
        
        const skuSource = html.getElementById(`Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        // main price
        if (destination && source) destination.innerHTML = source.innerHTML;

        // sticky atc
        if (stickyAtcSeparatePriceDestination && stickyAtcSeparatePriceSource) stickyAtcSeparatePriceDestination.innerHTML = stickyAtcSeparatePriceSource.innerHTML;
        if (stickyAtcPriceDestination && stickyAtcPriceSource) stickyAtcPriceDestination.innerHTML = stickyAtcPriceSource.innerHTML;
        if (stickyAtcImageDestination && stickyAtcImageSource) stickyAtcImageDestination.src = stickyAtcImageSource.src;
        // main atc
        if (mainAtcPriceSource && mainAtcPriceDestination) mainAtcPriceDestination.innerHTML = mainAtcPriceSource.innerHTML;

        // variant custom labels
        if (customLabelsDestination && customLabelsSource) {
          for (var i=0; i<customLabelsDestination.length; i++) {
            customLabelsDestination[i].innerHTML = customLabelsSource[i].innerHTML
          }
        }
        
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        if (this.QuantityBreaks) {
          const quantityBreaksSource = html.getElementById(`quantity-breaks-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
          // dynamic prices
          const dynamicPricesDestination = this.QuantityBreaks.querySelectorAll('.dynamic-price');
          const dynamicPricesSource = quantityBreaksSource.querySelectorAll('.dynamic-price');
          for (let i = 0; i < dynamicPricesDestination.length; i++) {
            dynamicPricesDestination[i].innerHTML = dynamicPricesSource[i].innerHTML;
          }
          
          // variant selectors
          if (this.QuantityBreaks.hasVariants) {
            this.QuantityBreaks.variantSelects.forEach(select => {
              select.dataset.selectedId = this.currentVariant.id;
            })
            const quantityBreakSelectsDestination = this.QuantityBreaks.querySelectorAll('.quantity-break__variant-select');
            const quantityBreakSelectsSource = quantityBreaksSource.querySelectorAll('.quantity-break__variant-select');
            for (let i=0; i<quantityBreakSelectsDestination.length; i++) {
              quantityBreakSelectsDestination[i].innerHTML = quantityBreakSelectsSource[i].innerHTML;
            }
            this.QuantityBreaks.initVariants();
          };
        }

        if (this.hasQuantityBreaksPicker) {
          const quantityBreaksSource = html.getElementById(`variant-selects-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
          // dynamic prices
          const dynamicPricesDestination = this.querySelectorAll('.dynamic-price');
          const dynamicPricesSource = quantityBreaksSource.querySelectorAll('.dynamic-price');
          for (let i = 0; i < dynamicPricesDestination.length; i++) {
            dynamicPricesDestination[i].innerHTML = dynamicPricesSource[i].innerHTML;
          }
          // variant images
          if (this.quantityBreaksPickerStyle === 'vertical' && this.quantityBreaksPickerDisplayedImages === 'variant_images') {
            const dynamicImagesDestination = this.querySelectorAll('.quantity-break__image img');
            const dynamicImagesSource = quantityBreaksSource.querySelectorAll('.quantity-break__image img');
            for (let i = 0; i < dynamicImagesDestination.length; i++) {
              dynamicImagesDestination[i].src = dynamicImagesSource[i].src;
            }
          }
        }

        if (this.secondarySelect) {
          const secondarySelectSource = html.getElementById(`${this.secondarySelectSelector}${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
          if (secondarySelectSource) this.secondarySelect.innerHTML = secondarySelectSource.innerHTML;
        }
        
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) price.classList.remove('visibility-hidden');

        this.dataset.options = this.currentVariant.options.join(',');

        if (inventoryDestination) inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true, window.variantStrings.soldOut);

        publish(PUB_SUB_EVENTS.variantChange, {data: {
          sectionId,
          html,
          variant: this.currentVariant
        }});
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const atcButtons = document.querySelectorAll('.main-product-atc');
    var addButtonText = productForm.querySelector('.main-atc__label__text');
    if (!addButtonText) addButtonText = productForm.querySelector('.main-atc__label');
    const atcButtonPrice = productForm.querySelector('.main-atc-price');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      addButton.setAttribute('data-unavailable', 'true');
      if (text) addButtonText.textContent = text;
      atcButtons.forEach(btn => {
        btn.setAttribute('disabled', 'disabled');
      })
      if (atcButtonPrice) atcButtonPrice.classList.add('hidden');
    } else {
      addButton.setAttribute('data-unavailable', 'false');
      addButtonText.textContent = window.variantStrings.addToCart;
      if (atcButtonPrice) atcButtonPrice.classList.remove('hidden');
      if (addButton.dataset.requiredFields === addButton.dataset.validFields) {
        addButton.removeAttribute('disabled');
        atcButtons.forEach(btn => {
          btn.removeAttribute('disabled');
        })
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    var addButtonText = productForm.querySelector('.main-atc__label__text');
    if (!addButtonText) addButtonText = productForm.querySelector('.main-atc__label');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('variant-selects', VariantSelects);

class SecondaryVariantSelect extends VariantSelects {
  constructor() {
    super();
    this.secondarySelectSelector = 'variant-selects-';
    this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
    this.isSecondary = true;
  }

  updateOptions() {
    this.options = this.querySelector('select').value.split(',');
  }
}
customElements.define('secondary-variant-select', SecondaryVariantSelect);

class SecondaryVariantSelectSeparate extends VariantSelects {
  constructor() {
    super();
    this.secondarySelectSelector = 'variant-selects-';
    this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
    this.isSecondary = true;
  }
}
customElements.define('secondary-variant-select-separate', SecondaryVariantSelectSeparate);

class CardVariantSelects extends VariantSelects {
  constructor() {
    super();
    this.quickAddSubmit = document.getElementById(`${this.dataset.section}-submit`);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updateVariantStatuses();
    
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateVariantInput();
      this.updateQuickAddUrl();
      this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
    }
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const imageElement = document.getElementById(`${this.dataset.section}-image`);
    if (!imageElement) return;

    const media = this.currentVariant.featured_media.preview_image;
    const baseUrl = media.src.split('?')[0];
    const widths = [165, 360, 533, 720, 940, 1066];
    const srcset = widths
      .filter(width => media.width >= width)
      .map(width => `${baseUrl}?width=${width} ${width}w`)
      .join(', ');
  
    imageElement.srcset = `${srcset}, ${baseUrl} ${media.width}w`;
    imageElement.src = `${baseUrl}?width=533`;
  }

  updateQuickAddUrl() {
    if (!this.currentVariant || !this.quickAddSubmit) return;

    this.quickAddSubmit.dataset.productUrl = `${this.dataset.url}?variant=${this.currentVariant.id}`;
  }
}
customElements.define('card-variant-selects', CardVariantSelects);
